import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from '../../assets/styles/OrderSentSuccessPopup.module.scss';
import { playStatusChangeAlert } from '../../Helpers/soundAlerts';

/**
 * Popup de confirmação exibido quando o cliente envia pedidos para a cozinha.
 * Mostra os pratos enviados, status "Em preparo" piscando e botão "Entendi".
 */
const OrderSentSuccessPopup = ({ dishes = [], tableNumber = null, onClose }) => {
  useEffect(() => {
    // Tenta disparar feedback sonoro suave ao abrir
    try {
      playStatusChangeAlert();
    } catch (e) {
      // Ignora restrições de áudio
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        if (typeof onClose === 'function') onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const modalContent = (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget && typeof onClose === 'function') {
          onClose();
        }
      }}
    >
      <div className={styles.popupCard} role="dialog" aria-modal="true">
        {/* Ícone comemorativo de cozinha */}
        <div className={styles.iconWrapper}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z" />
            <line x1="6" y1="17" x2="18" y2="17" />
          </svg>
        </div>

        <h2 className={styles.title}>Pedido Enviado com Sucesso!</h2>

        {/* Status Em preparo piscando */}
        <div className={styles.statusBadge}>
          <span className={styles.pulseDot} />
          <span>Status: <strong>Em preparo</strong></span>
        </div>

        {/* Lista dos pratos que foram enviados */}
        <div className={styles.dishesContainer}>
          <div className={styles.dishesHeader}>
            <span>Pratos confirmados {tableNumber ? `(Mesa ${tableNumber})` : ''}</span>
            <span>{dishes.reduce((acc, d) => acc + (d.count || 1), 0)} {dishes.length === 1 && (dishes[0]?.count || 1) === 1 ? 'item' : 'itens'}</span>
          </div>

          <ul className={styles.dishesList}>
            {dishes.map((dish, index) => {
              const name = typeof dish === 'string' ? dish : (dish.name || dish.title || 'Prato');
              const count = typeof dish === 'object' && dish.count ? dish.count : 1;
              return (
                <li key={index} className={styles.dishItem}>
                  <div className={styles.dishInfo}>
                    {count > 1 && <span className={styles.dishQty}>{count}x</span>}
                    <span className={styles.dishName}>{name}</span>
                  </div>
                  <span className={styles.dishCheck} title="Enviado para a cozinha">✓</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Mensagem tranquilizadora para o cliente */}
        <div className={styles.messageBox}>
          <p className={styles.messageMain}>
            Aguarde um momento! Nossos cozinheiros já estão preparando o seu pedido com todo carinho e ele será entregue logo mais à sua mesa.
          </p>
          <p className={styles.messageSub}>
            💡 Fique tranquilo: seu pedido já foi registrado na comanda e não precisa ser enviado novamente.
          </p>
        </div>

        {/* Botão Entendi */}
        <button
          type="button"
          className={styles.btnUnderstood}
          onClick={onClose}
          autoFocus
        >
          Entendi
        </button>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default OrderSentSuccessPopup;
